import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useScrollReveal, fadeUpVariants, staggerContainerVariants, cardVariants } from '../hooks/useScrollReveal'
import { useAdmin } from '../context/AdminContext'
import { FaGraduationCap } from 'react-icons/fa'

const COLOR = {
  blue:  { bg:'rgba(59,130,246,.1)',   border:'rgba(59,130,246,.3)',   text:'#3b82f6', hex: '#3b82f6' },
  green: { bg:'rgba(16,185,129,.1)',   border:'rgba(16,185,129,.3)',   text:'#10b981', hex: '#10b981' },
  orange:{ bg:'rgba(245,158,11,.1)',   border:'rgba(245,158,11,.3)',   text:'#f59e0b', hex: '#f59e0b' },
  yellow:{ bg:'rgba(234,179,8,.1)',    border:'rgba(234,179,8,.3)',    text:'#eab308', hex: '#eab308' },
  purple:{ bg:'rgba(168,85,247,.1)',   border:'rgba(168,85,247,.3)',   text:'#a855f7', hex: '#a855f7' },
  cyan:  { bg:'rgba(6,182,212,.1)',    border:'rgba(6,182,212,.3)',    text:'#06b6d4', hex: '#06b6d4' },
}

function EduCard({ edu }) {
  const c = COLOR[edu.color] || COLOR.purple

  return (
    <motion.div variants={cardVariants} className="relative pl-8 md:pl-12 pb-12 last:pb-0">
      
      {/* Timeline Dot */}
      <div 
        className="absolute left-[-5px] top-1.5 w-3 h-3 rounded-full z-10"
        style={{ 
          backgroundColor: c.hex,
          boxShadow: `0 0 0 4px #0B0F19, 0 0 12px ${c.hex}` 
        }}
      />

      {/* Card Content */}
      <div 
        className="card p-6 md:p-8 flex flex-col gap-4 transition-all duration-300"
        style={{ borderLeft: `2px solid transparent` }}
        onMouseEnter={(e) => e.currentTarget.style.borderLeft = `2px solid ${c.hex}`}
        onMouseLeave={(e) => e.currentTarget.style.borderLeft = `2px solid transparent`}
      >
        <div className="flex flex-col items-start gap-4">
          
          {/* Date Pill */}
          <div 
            className="px-3 py-1 rounded-full font-mono text-xs font-bold tracking-wider"
            style={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}` }}
          >
            {edu.date}
          </div>

          <div className="flex-1">
            {/* Degree Title */}
            <h3 className="text-xl md:text-2xl font-display font-semibold text-white mb-2">
              {edu.degree}
            </h3>
            
            {/* Institution */}
            <div className="flex items-center gap-2 mb-4 font-semibold text-sm" style={{ color: c.text }}>
              <span className="text-lg">{edu.icon && edu.icon.length < 5 ? edu.icon : <FaGraduationCap />}</span>
              <span>{edu.institution}</span>
            </div>

            {/* Description */}
            {edu.description && (
              <p className="text-slate-400 text-sm leading-relaxed max-w-3xl">
                {edu.description}
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default function Education() {
  const { ref, controls } = useScrollReveal()
  const { siteData, loading } = useAdmin()

  if (loading) return null

  return (
    <section id="education" className="py-28 relative overflow-hidden flex flex-col items-center">
      <Helmet>
        <title>Education | Shiv Shankar Pandit</title>
        <meta name="description" content="Academic journey & professional qualifications." />
      </Helmet>

      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/4 w-96 h-96 rounded-full blur-[120px] opacity-[0.05] pointer-events-none bg-purple-500" />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12 w-full">
        <motion.div variants={staggerContainerVariants} initial="hidden" animate="visible">
          
          {/* Header */}
          <motion.div variants={fadeUpVariants} className="mb-16 text-center">
            <h1 className="section-heading mb-4 text-4xl md:text-5xl">My <span className="gradient-text">Education</span></h1>
            <p className="text-muted text-base max-w-md mx-auto">Academic journey & professional qualifications</p>
          </motion.div>

          {/* Timeline Container */}
          <div className="relative">
            {/* The Vertical Line */}
            {siteData.educations.length > 0 && (
              <div className="absolute left-0 top-2 bottom-0 w-[2px] bg-white/10" />
            )}

            <motion.div variants={staggerContainerVariants} className="flex flex-col">
              {siteData.educations.map(e => <EduCard key={e.id} edu={e} />)}
            </motion.div>
            
            {siteData.educations.length === 0 && (
              <p className="text-center text-muted font-mono text-sm py-12">No educations yet.</p>
            )}
          </div>

        </motion.div>
      </div>
    </section>
  )
}
